// react
import { MouseEvent } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useNavigate } from 'react-router-dom';

// types
import { Contract } from '../../../@types';

// mui
import { Box, Button, Grid, Paper, Typography } from '@mui/material';

// utils
import { formatCurrency } from '../../../utils/currency';
import { formatDate } from '../../../utils/dates';

// theme
import { theme } from '../../../theme';

// ui
import { ChipStatus } from '../../ui/ChipStatus/ChipStatus';
import { IconCircle } from '../../ui/IconCircle/IconCircle';

// config
import { featureFlags } from '../../../config/featureFlags';

// props
export type CardContractProps = {
	contract: Contract;
	onSubmitInvoice?: (contract: Contract) => void;
};

export const CardContract = ({ contract, onSubmitInvoice }: CardContractProps) => {
	// hooks
	const navigate = useNavigate();
	const { t } = useTranslation();

	// handlers
	const goToDetail = () => navigate(`/contracts/${contract.id}`);

	return (
		<Paper
			onClick={(_e: MouseEvent<HTMLDivElement>) => goToDetail()}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					goToDetail();
				}
			}}
			role="button"
			tabIndex={0}
			sx={{
				overflow: 'hidden',
				cursor: 'pointer',
			}}>
			<Box
				sx={{
					display: 'block',
					padding: theme.spacing(1.5, 2),
				}}>
				<Grid container spacing={2}>
					<Grid item xs={true}>
						<Grid alignItems="center" container spacing={2}>
							<Grid item xs={true}>
								<Grid container spacing={2}>
									{contract.status && (
										<Grid item xs={12}>
											<ChipStatus status={contract.status} />
										</Grid>
									)}
									<Grid item xs={12}>
										<Typography component="div" fontWeight={700} noWrap variant="h2">
											{contract.num}
										</Typography>
										{contract.amounts.map((amount) => (
											<Typography key={`contract-amount-${amount.id}`} fontWeight={600}>
												{formatCurrency({ currency: amount.currency, value: amount.amount })}
											</Typography>
										))}
									</Grid>
									{contract.dateSigning && (
										<Grid item xs={12}>
											<Typography color="brand.grey.500" fontWeight={600} variant="caption">
												{formatDate({ date: new Date(contract.dateSigning) })}
											</Typography>
										</Grid>
									)}
								</Grid>
							</Grid>
							<Grid item xs="auto">
								<IconCircle size="sm" type="next" />
							</Grid>
						</Grid>
					</Grid>
					{featureFlags['CONTRACT_WORKFLOW'] && onSubmitInvoice && (
						<Grid item xs={12}>
							<Button
								fullWidth
								variant="contained"
								onClick={(e) => {
									e.stopPropagation();
									onSubmitInvoice(contract);
								}}>
								{t('submitInvoice')}
							</Button>
						</Grid>
					)}
				</Grid>
			</Box>
		</Paper>
	);
};
